/**
 * Requiredness of embedded dynamic widget fields.
 *
 * The canonical flag is the BASE `config_data.is_required` (the site validates only that,
 * see `EmbeddedDynamicWidgetRenderer` validateState); `required` is a deprecated alias the
 * MCP contract still reads as a fallback (`functions/services/mcp/workflowInputContract.ts`).
 * Controller variants never change requiredness, so `variants` is never touched here.
 */

export type DynamicFieldConfig = Record<string, any>;

// Hidden constants are never user-interactive, and a seed is always generated when absent.
const NO_REQUIRED_FLAG_TYPES = new Set(['hidden_text', 'hidden_number', 'hidden_prompt', 'seed']);

export function supportsRequiredFlag(fieldType: string): boolean {
  return !NO_REQUIRED_FLAG_TYPES.has(fieldType);
}

/** Mirrors the server reading (`is_required ?? required`, boolean or 0/1). */
export function isDynamicFieldRequired(config: DynamicFieldConfig | null | undefined): boolean {
  const raw = config?.is_required ?? config?.required;
  return raw === true || raw === 1;
}

/**
 * Returns a new config with the flag set or cleared. Setting writes `is_required: true`;
 * clearing removes the key instead of writing `false`, so a config that was never marked
 * stays byte-identical. Both paths drop the deprecated `required` alias, otherwise unchecking
 * a legacy `required: true` field would leave it required through the server fallback.
 */
export function setDynamicFieldRequired(config: DynamicFieldConfig, required: boolean): DynamicFieldConfig {
  const { is_required: _isRequired, required: _legacyRequired, ...rest } = config;
  return required ? { ...rest, is_required: true } : rest;
}
